import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Cloud, ChevronDown, Check, X } from 'lucide-react';
import { useEditorStore } from '../store/editorStore';
import { ExportButton } from './ExportButton';
import { AspectRatioSelector } from './AspectRatioSelector';

export function EditorHeader() {
  const { t } = useTranslation();
  const projectName = useEditorStore((s) => s.project.name);
  const setProject = useEditorStore((s) => s.setProject);
  const project = useEditorStore((s) => s.project);

  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(projectName);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    if (!showDropdown) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  const commitRename = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== projectName) {
      setProject({ ...project, name: trimmed, updatedAt: Date.now() });
    }
    setEditing(false);
  };

  const cancelRename = () => {
    setEditValue(projectName);
    setEditing(false);
  };

  const startRename = () => {
    setEditValue(projectName);
    setEditing(true);
    setShowDropdown(false);
  };

  const btnClass = 'p-1.5 rounded-[var(--radius-chip)] hover:bg-[var(--user-nav-hover-bg)] text-[var(--user-page-text)] opacity-70 hover:opacity-100 transition-all disabled:opacity-30 disabled:cursor-not-allowed';

  return (
    <div className="flex items-center justify-between h-14 px-3 bg-[#252529] border-b border-[#2A2A2E] shrink-0 select-none">
      {/* Left: project name */}
      <div className="flex items-center gap-3 min-w-0">
        <Cloud size={14} className="text-[var(--user-page-text)] opacity-50 shrink-0" />
        {editing ? (
          <div className="flex items-center gap-1">
            <input
              ref={inputRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') cancelRename();
              }}
              onBlur={commitRename}
              className="bg-transparent border border-[var(--user-page-card-border)] rounded px-1.5 py-0.5 text-sm text-[var(--user-page-text)] outline-none focus:border-[var(--user-page-accent)] w-40"
            />
            <button onClick={commitRename} className={btnClass}><Check size={14} /></button>
            <button onClick={cancelRename} className={btnClass}><X size={14} /></button>
          </div>
        ) : (
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-1 text-sm font-medium text-[var(--user-page-text)] hover:opacity-80 transition-opacity cursor-pointer truncate max-w-[200px]"
            >
              <span className="truncate">{projectName}</span>
              <ChevronDown size={12} className="opacity-50 shrink-0" />
            </button>
            {showDropdown && (
              <div
                ref={dropdownRef}
                className="absolute top-full mt-1 start-0 bg-[#1C1C1E] border border-[#3A3A3C] rounded-lg shadow-xl z-50 min-w-[160px] py-1"
              >
                <button
                  onClick={startRename}
                  className="w-full text-start px-3 py-1.5 text-sm text-[#E5E5EA] hover:bg-[#2C2C2E] cursor-pointer"
                >
                  {t('editor.rename', 'Rename')}
                </button>
                <button
                  disabled
                  className="w-full text-start px-3 py-1.5 text-sm text-[#636366] cursor-not-allowed"
                >
                  {t('editor.duplicateTo', 'Duplicate to')}
                </button>
                <button
                  disabled
                  className="w-full text-start px-3 py-1.5 text-sm text-[#636366] cursor-not-allowed"
                >
                  {t('editor.moveTo', 'Move to')}
                </button>
              </div>
            )}
          </div>
        )}
        <AspectRatioSelector />
      </div>

      {/* Right: export */}
      <div className="flex items-center gap-2">
        <ExportButton />
      </div>
    </div>
  );
}
